import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import { OrganizationSubscription } from '@manifest-cyber/types/interface/db';
import { Card } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useTranslation } from 'react-i18next';
import { ProductIcon } from '../../../components/Icons/ProductIcon/ProductIcon';
import ManifestModal from '../../../components/ManifestModal/ManifestModal';
import { ManifestNotifications } from '../../../hooks/utils/useNotifications';
import {
  AdministrationApi,
  PutOrganizationSubscriptionPayload,
} from '../api/administration.api';
import { makeQueryKey } from '../../../hooks/queries/useFetchOrganizationSubscriptions';
import styles from './OrganizationSubscriptionModal.module.scss';
import { useQueryClient } from '@tanstack/react-query';

const DEFAULT_ORG_NAME = '!ORG_NAME_MISSING!';

type OrganizationSubscriptionModalProps = {
  organization: {
    _id: string;
    name: string;
    subscriptions?: OrganizationSubscription;
  };
  onSuccess: () => void;
  opened: boolean;
  onClose: () => void;
};

type OrganizationSubscriptionDropdownItem = {
  key: string;
  description: string;
  value: OrganizationSubscription;
};

const DROPDOWN_ITEMS: OrganizationSubscriptionDropdownItem[] = [
  {
    key: 'page.administration.subscription-modal.sbomOnly',
    description: 'page.administration.subscription-modal.sbomOnlyDescription',
    value: {
      aibom: false,
      sbom: true,
    },
  },
  {
    key: 'page.administration.subscription-modal.aibomOnly',
    description: 'page.administration.subscription-modal.aibomOnlyDescription',
    value: {
      aibom: true,
      sbom: false,
    },
  },
  {
    key: 'page.administration.subscription-modal.sbomAndAibom',
    description: 'page.administration.subscription-modal.sbomAndAibomDescription',
    value: {
      aibom: true,
      sbom: true,
    },
  },
];

const SubscriptionCard = ({
  item,
  isSelected,
  onSelect,
  t,
}: {
  item: OrganizationSubscriptionDropdownItem;
  isSelected: boolean;
  onSelect: () => void;
  t: (key: string) => string;
}) => (
  <Card
    key={item.key}
    onClick={onSelect}
    className={styles.orgSubscriptionSelectorItem}
    role="radio"
    aria-checked={isSelected}
    tabIndex={0}
    onKeyPress={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        onSelect();
      }
    }}
  >
    <div>
      <TextComponent variant="bodyMedium">{t(item.key)}</TextComponent>
      <br />
      <TextComponent variant="bodySmall" color="muted">
        {t(item.description)}
      </TextComponent>
    </div>
    <div>{isSelected && <ProductIcon />}</div>
  </Card>
);

export function OrganizationSubscriptionModal(props: OrganizationSubscriptionModalProps) {
  const { t } = useTranslation();
  const orgId = props.organization._id?.toString() ?? '';
  const orgLabel = `${props.organization.name || DEFAULT_ORG_NAME} (${orgId})`;
  const [selected, setSelected] = useState<OrganizationSubscription | undefined>(
    props.organization.subscriptions,
  );
  const queryClient = useQueryClient();
  const translatedDropdownItems = useMemo(
    () =>
      DROPDOWN_ITEMS.map((item) => ({
        ...item,
        title: t(item.key),
        description: t(item.description),
      })),
    [t],
  );  const { mutate, isLoading } = useMutation({
    mutationFn: (payload: PutOrganizationSubscriptionPayload) =>
      AdministrationApi.postOrganizationSubscription(payload),
    onSuccess: (_data) => {
      props.onClose();
      props.onSuccess();
      queryClient.invalidateQueries({
        queryKey: makeQueryKey({ organizationId: orgId }),
      });
      // Invalidate user cache since subscription data comes from /user/self endpoint
      queryClient.invalidateQueries(['manifest-user']);
      // Also invalidate organization cache for any components that use useFetchOrganization
      queryClient.invalidateQueries(['currentOrganization']);
    },
    onError: (_error) => {
      ManifestNotifications.showErrorNotification({
        title: t('global.unexpectedError'),
        message: t('global.unexpectedErrorSubtitle'),
      });
    },
  });

  const handleUpdate = () => {
    if (selected) {
      mutate({ orgId, subscriptions: selected });
    }
  };

  return (
    <ManifestModal
      title={t('page.administration.subscription-modal.title')}
      opened={props.opened}
      onClose={props.onClose}
      size={'md'}
      primaryAction={{
        label: t('global.update'),
        isLoading: isLoading,
        isDisabled: isEqual(props.organization.subscriptions, selected),
        onClick: handleUpdate,
      }}
    >
      <ul className={styles.orgSubscriptionSelectorList}>
        <TextComponent variant="bodySmall" color="muted">
          {orgLabel}
        </TextComponent>
        {translatedDropdownItems.map((item) => (
          <SubscriptionCard
            key={item.key}
            item={item}
            isSelected={isEqual(item.value, selected)}
            onSelect={() => setSelected(item.value)}
            t={t}
          />
        ))}
      </ul>
    </ManifestModal>
  );
}
